/** @typedef {'new' | 'ending_soon' | 'expired' | null} OfferStatusKey */

export const OFFER_STATUS_LABELS = {
  new: 'New',
  ending_soon: 'Ending Soon',
  expired: 'Expired',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Business calendar for offer badges — Kenya, no DST. */
export const OFFER_TIME_ZONE = 'Africa/Nairobi';

function parseDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function zonedYmd(date, timeZone = OFFER_TIME_ZONE) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Whole calendar days from `from` to `to` in the offer timezone.
 * Same calendar day → 0; yesterday → -1; tomorrow → 1.
 */
export function calendarDaysBetween(from, to, timeZone = OFFER_TIME_ZONE) {
  const [y1, m1, d1] = zonedYmd(from, timeZone).split('-').map(Number);
  const [y2, m2, d2] = zonedYmd(to, timeZone).split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / MS_PER_DAY);
}

function elapsedDays(a, b) {
  return (b.getTime() - a.getTime()) / MS_PER_DAY;
}

function getNewBadgeDays(product) {
  const n = Number(product?.newBadgeDays);
  return Number.isFinite(n) && n >= 0 ? n : 2;
}

function getEndingSoonBadgeDays(product) {
  const n = Number(product?.endingSoonBadgeDays);
  return Number.isFinite(n) && n >= 0 ? n : 2;
}

/**
 * Status for a single store listing.
 * Priority: expired > ending_soon > new > none
 *
 * Offer end dates are calendar dates in Africa/Nairobi: a deal whose end
 * date is today stays live until the end of that day, even if the stored
 * time is midnight or earlier today.
 */
export function getOfferStatusForListing(product, now = new Date()) {
  if (!product) return null;

  const endsAt = parseDate(product.offerEndsAt);
  if (endsAt) {
    const daysLeft = calendarDaysBetween(now, endsAt);
    if (daysLeft < 0) {
      return 'expired';
    }

    const endingSoonDays = getEndingSoonBadgeDays(product);
    if (daysLeft <= endingSoonDays) {
      return 'ending_soon';
    }
  }

  const startAt = parseDate(product.offerStartsAt) || parseDate(product.createdAt);
  if (startAt) {
    const newBadgeDays = getNewBadgeDays(product);
    const ageDays = elapsedDays(startAt, now);
    if (ageDays >= 0 && ageDays <= newBadgeDays) {
      return 'new';
    }
  }

  return null;
}

export function isOfferExpired(product, now = new Date()) {
  return getOfferStatusForListing(product, now) === 'expired';
}

/**
 * Best active status for grouped / grid cards (multi-store).
 * Ignores expired listings; picks ending_soon over new.
 */
export function getCardOfferStatus(product, now = new Date()) {
  const offers = product?.offerGroup?.offers?.length
    ? product.offerGroup.offers
    : [product];

  const active = offers.filter((o) => !isOfferExpired(o, now));
  if (!active.length) {
    return 'expired';
  }

  const statuses = active.map((o) => getOfferStatusForListing(o, now)).filter(Boolean);
  if (statuses.includes('ending_soon')) return 'ending_soon';
  if (statuses.includes('new')) return 'new';
  return null;
}

export function getOfferStatusLabel(status) {
  return status ? OFFER_STATUS_LABELS[status] || null : null;
}

export function isOfferEndingSoon(product, now = new Date()) {
  return getOfferStatusForListing(product, now) === 'ending_soon';
}

/** True when any active listing in a grouped card is ending soon. */
export function isCardEndingSoon(product, now = new Date()) {
  const offers = product?.offerGroup?.offers?.length
    ? product.offerGroup.offers
    : [product];
  return offers.some((o) => getOfferStatusForListing(o, now) === 'ending_soon');
}

/** True when any active listing in a grouped card has the New badge. */
export function isCardNew(product, now = new Date()) {
  const offers = product?.offerGroup?.offers?.length
    ? product.offerGroup.offers
    : [product];
  return offers.some((o) => getOfferStatusForListing(o, now) === 'new');
}

/** True when every listing in a grouped card is expired. */
export function isCardExpired(product, now = new Date()) {
  return getCardOfferStatus(product, now) === 'expired';
}
